import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  FileText, 
  Link2, 
  Users, 
  Building2, 
  Crown, 
  Loader2, 
  Sparkles,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Zap
} from "lucide-react";

import { API_URL } from "@/utils/api";

export default function UsageLimitsWidget({ user }) {
  const [limitsData, setLimitsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLimits();
  }, []);

  const fetchLimits = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/user/limits`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setLimitsData(data);
      }
    } catch (err) {
      console.error("Failed to fetch limits:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl bg-white">
        <CardContent className="p-6 flex justify-center items-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#BFFF00]" />
        </CardContent>
      </Card>
    );
  }

  if (!limitsData) return null;

  const { plan, plan_name, limits, usage, remaining, platform_fee_percent, show_upgrade_prompt } = limitsData;
  
  const limitItems = [
    { 
      key: "invoices", 
      label: "Invoices", 
      icon: FileText, 
      color: "blue",
      path: "/dashboard/invoicing"
    },
    { 
      key: "payment_links", 
      label: "Payment Links", 
      icon: Link2, 
      color: "purple",
      path: "/dashboard/payment-links"
    },
    { 
      key: "customers", 
      label: "Customers", 
      icon: Users, 
      color: "green",
      path: "/dashboard/settings/recipients"
    },
    { 
      key: "vendors", 
      label: "Vendors", 
      icon: Building2, 
      color: "amber",
      path: "/dashboard/settings/recipients"
    }
  ];

  const getProgressColor = (used, limit) => {
    if (limit === -1) return "bg-[#BFFF00]"; // Unlimited
    const percentage = (used / limit) * 100;
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 70) return "bg-amber-500";
    return "bg-[#BFFF00]";
  };

  const getPercentage = (used, limit) => {
    if (limit === -1) return 100; // Show full bar for unlimited
    return Math.min(100, (used / limit) * 100);
  };

  const isNearLimit = (key) => {
    const limit = limits[key];
    const used = usage[key];
    if (limit === -1) return false;
    return (used / limit) >= 0.8;
  };

  const isAtLimit = (key) => {
    const limit = limits[key];
    const used = usage[key];
    if (limit === -1) return false;
    return used >= limit;
  };

  // Count items near or at limit
  const warningCount = limitItems.filter(item => isNearLimit(item.key) && !isAtLimit(item.key)).length;
  const criticalCount = limitItems.filter(item => isAtLimit(item.key)).length;

  return (
    <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl bg-white overflow-hidden" data-testid="usage-limits-widget">
      <CardHeader className="pb-2 md:pb-3 bg-gradient-to-r from-[#F7FFE5] to-white px-3 md:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <Zap className="w-4 h-4 md:w-5 md:h-5 text-[#121212]" />
            </div>
            <div>
              <CardTitle className="text-sm md:text-base font-bold font-['Manrope']">Your Usage</CardTitle>
              <div className="flex items-center gap-1.5 md:gap-2 mt-0.5">
                <Badge className={`text-xs rounded-full px-2 ${
                  plan === 'enterprise' ? 'bg-[#BFFF00]/30 text-[#121212]' :
                  plan === 'power' ? 'bg-purple-100 text-purple-700' :
                  plan === 'scale' ? 'bg-blue-100 text-blue-700' :
                  plan === 'growth' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {plan_name}
                </Badge>
                <span className="text-xs text-gray-500">{platform_fee_percent}%</span>
              </div>
            </div>
          </div>
          {criticalCount > 0 && (
            <Badge className="bg-red-100 text-red-700 rounded-full animate-pulse text-xs px-2">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {criticalCount}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-3 md:pt-4 space-y-3 md:space-y-4 px-3 md:px-6">
        {/* Limit Items */}
        <div className="grid grid-cols-2 gap-2 md:gap-3">
          {limitItems.map((item) => {
            const limit = limits[item.key];
            const used = usage[item.key] || 0;
            const isUnlimited = limit === -1;
            const atLimit = isAtLimit(item.key);
            const nearLimit = isNearLimit(item.key);
            const Icon = item.icon;

            return (
              <Link to={item.path} key={item.key} className="block">
                <div 
                  className={`p-2 md:p-3 rounded-xl border-2 transition-all hover:shadow-md ${
                    atLimit ? 'border-red-200 bg-red-50' :
                    nearLimit ? 'border-amber-200 bg-amber-50' :
                    'border-gray-100 bg-white hover:border-[#BFFF00]/50'
                  }`}
                  data-testid={`usage-${item.key}`}
                >
                  <div className="flex items-center justify-between mb-1.5 md:mb-2">
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <Icon className={`w-3 h-3 md:w-4 md:h-4 ${
                        atLimit ? 'text-red-500' :
                        nearLimit ? 'text-amber-500' :
                        'text-gray-500'
                      }`} />
                      <span className="text-xs md:text-sm font-medium text-gray-700">{item.label}</span>
                    </div>
                    {isUnlimited ? (
                      <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500" />
                    ) : atLimit ? (
                      <AlertTriangle className="w-3 h-3 md:w-4 md:h-4 text-red-500" />
                    ) : null}
                  </div>
                  
                  <div className="flex items-baseline justify-between mb-1">
                    <span className={`text-base md:text-lg font-bold ${
                      atLimit ? 'text-red-600' :
                      nearLimit ? 'text-amber-600' :
                      'text-[#121212]'
                    }`}>
                      {used}
                    </span>
                    <span className="text-xs text-gray-500">
                      {isUnlimited ? (
                        <span className="text-green-600 font-medium text-xs">Unlimited</span>
                      ) : (
                        `/ ${limit}`
                      )}
                    </span>
                  </div>
                  
                  <Progress 
                    value={getPercentage(used, limit)} 
                    className="h-1 md:h-1.5"
                    indicatorClassName={getProgressColor(used, limit)}
                  />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Upgrade CTA */}
        {show_upgrade_prompt && (criticalCount > 0 || warningCount > 0) && (
          <div className="p-4 bg-gradient-to-r from-[#F7FFE5] to-[#EDFFC2] rounded-xl border-2 border-[#BFFF00]/30">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 text-[#121212]" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-[#121212] text-sm">
                  {criticalCount > 0 ? "Upgrade to Continue" : "Running Low on Limits"}
                </h4>
                <p className="text-xs text-[#121212]/70 mt-1">
                  {criticalCount > 0 
                    ? "You've reached your limit. Upgrade your plan to create more."
                    : "You're approaching your limits. Upgrade for unlimited access."}
                </p>
                <Link to="/dashboard/plans">
                  <Button 
                    size="sm" 
                    className="mt-3 bg-[#121212] hover:bg-[#2a2a2a] rounded-full text-xs"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    View Plans
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* All Good Badge for unlimited users */}
        {!show_upgrade_prompt && (
          <div className="flex items-center justify-center gap-2 py-2 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Unlimited usage on your plan</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
